import {ProviderController,} from "../views/abstract-component-view.js";
import {css, html, unsafeHTML, LitElement, repeat} from '../libs/lit.dist.js';
import {ToastMessagesProvider} from '../toast-messages/toast-messages.js';
import {TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING, TOAST_TYPE_INFO, DEFAULT_DURATION} from "../toast-messages/toast-messages-config.js";
import './pk-icon.js';
import './pk-spinner.js';

export class PkToastMessage extends LitElement {

    static defaultIconType = 'regular';
    static defaultSuccessIcon = 'badge-check';
    static defaultSuccessIconType = 'solid';
    static defaultWarningIcon = 'exclamation-triangle';
    static defaultWarningIconType = 'regular';
    static defaultErrorIcon = 'exclamation-circle';
    static defaultErrorIconType = 'solid';

    static styles = css`
        :host {
            margin: 0 auto;
        }

        .toast--danger {
            --color: rgb(145, 0, 0);
            --background: rgba(255, 207, 207, 0.8);
            --button-background: rgba(220, 60, 60, 0.15);
            --button-background-hover: rgba(220, 60, 60, 0.25);
        }

        .toast {
            &.fade-out {
                .toast-body {
                    transition: all 0.3s;
                    opacity: 0;
                    scale: 0.9;
                    @starting-style {
                        scale: 1;
                    }
                }
            }
        }

        .toast-body {
            pointer-events: all;
            display: flex;
            gap: 0.5rem;
            align-items: center;
            width: auto;
            min-width: 5rem;
            margin: 0 auto;
            padding: 0.75rem 2rem;
            backdrop-filter: blur(0.5rem);
            border-radius: 2rem;
            box-shadow: 0 2px 1px rgba(0, 0, 0, 0.33);
            background: var(--background, rgb(70 70 70 / 0.8));
            color: var(--color, white);
            --link-color: var(--color, white);
            font-weight: 400;
            translate: 0 0;
            transition: all 0.5s;
            opacity: 1;
            @starting-style {
                opacity: 0;
                translate: 0 100%;
            }
        }

        .close-button {
            font-weight: 500;
            border: 0 none;
            background: none;
            color: var(--color, white);
            border-radius: 50%;
            width: 2rem;
            height: 2rem;
            line-height: 2rem;
            padding: 0;
            margin: -0.5rem -1.5rem -0.5rem auto;
            flex-shrink: 0;
            transition: background 0.4s;
            &:hover {
                transition: background 0.4s;
                background: var(--button-background, rgb(255 255 255 / 0.25));
            }
        }
        
        slot[name="button"]::slotted(button) {
            font-weight: 400;
            margin: -0.5rem 0 -0.5rem 1rem;
            padding: 0.5rem 0.75rem;
            color: var(--color, white);
            border: 0 none;
            border-radius: 0.4rem;
            background: var(--button-background, rgb(255 255 255 / 0.15));
            transition: background 0.4s;
        }

        slot[name="button"]::slotted(button:hover) {
            transition: background 0.4s;
            background: var(--button-background-hover, rgb(255 255 255 / 0.25));
        }

        .icon {
            align-self: center;
            font-size: 1rem;
            width: 1.2rem;
            height: 1.2rem;
            margin-left: -0.5rem;
            margin-right: 0.25rem;

            &[icon=""] {
                display: none !important;
            }
        }

        pk-spinner {
            --size: 1.2rem;
            --width: 0.2rem;
            align-self: center;
            margin-left: -0.5rem;
            margin-right: 0.25rem;
        }
    `;

    static properties = {
        type: {type: String},
        duration: {type: Number},
        icon: {type: String},
        iconType: {type: String},
        iconCollection: {type: String},
        open: {type: Boolean},
        closable: {type: Boolean},
        spinner: {type: Boolean},
    };

    #timeoutTimer;
    #timeoutTimeElapsed = 0;
    #timeoutTimerStartedAt = 0;

    constructor() {
        super();
        this.duration = DEFAULT_DURATION;
        this.type = TOAST_TYPE_INFO;
        this.open = true;
    }

    connectedCallback() {
        super.connectedCallback();
        this.ariaLive = 'assertive';
        this.tabinex = '0';
        this.role = 'alert'
        this.addEventListener('mouseenter', this.#stopHideTimeout);
        this.addEventListener('mouseleave', this.#startHideTimeout);
    }

    #onTransitionEnd(event) {
        if (event.propertyName === 'opacity' && !this.open) {
            this.dispatchEvent(new Event('hidden', {bubbles: true}));
        }
        if (event.propertyName === 'opacity' && this.open) {
            this.#startHideTimeout();
        }
    }

    #stopHideTimeout() {
        clearTimeout(this.#timeoutTimer);
        if (this.#timeoutTimerStartedAt > 0) {
            this.#timeoutTimeElapsed += (Date.now() - this.#timeoutTimerStartedAt);
        }
        this.#timeoutTimerStartedAt = 0;
    }

    #startHideTimeout() {
        this.#stopHideTimeout();
        if (this.duration < Infinity) {
            this.#timeoutTimerStartedAt = Date.now();
            this.#timeoutTimer = setTimeout(() => {
                this.#hide();
            }, Math.max(0, this.duration - this.#timeoutTimeElapsed));
        }
    }

    #hide() {
        this.open = false;
    }

    updated(t) {
        super.updated(t);
        if (t.has('open')) {
            if (!this.open) {
                this.dispatchEvent(new Event('hide', {bubbles: true}));
            }
        }
    }

    get #iconName() {
        if (this.icon) {
            return this.icon;
        }
        switch (String(this.type).toUpperCase()) {
            case TOAST_TYPE_SUCCESS:
                return PkToastMessage.defaultSuccessIcon;
            case TOAST_TYPE_WARNING:
                return PkToastMessage.defaultWarningIcon;
            case TOAST_TYPE_DANGER:
                return PkToastMessage.defaultErrorIcon;
        }
        return '';
    }

    get #iconTypeName() {
        if (this.iconType) {
            return this.iconType;
        }
        switch (String(this.type).toUpperCase()) {
            case TOAST_TYPE_SUCCESS:
                return PkToastMessage.defaultSuccessIconType;
            case TOAST_TYPE_WARNING:
                return PkToastMessage.defaultWarningIconType;
            case TOAST_TYPE_DANGER:
                return PkToastMessage.defaultErrorIconType;
        }
        return '';
    }

    render() {
        return html`
            <div class="toast toast--${String(this.type).toLowerCase()} ${!this.open ? ' fade-out' : ''}">
                <div
                    class="toast-body"
                    @transitionend="${this.#onTransitionEnd}"
                    @transitioncancel="${this.#onTransitionEnd}"
                >
                    <slot name="icon">
                        ${this.spinner ? html`
                            <pk-spinner></pk-spinner>
                        ` : html`
                            <pk-icon
                                class="icon"
                                icon="${this.#iconName}"
                                type="${this.#iconTypeName || PkToastMessage.defaultIconType}"
                                collection="${this.iconCollection}"
                            ></pk-icon>
                        `}
                    </slot>
                    <slot></slot>
                    <slot name="button"></slot>
                    ${this.closable !== false ? html`
                        <button @click="${this.#hide}" class="close-button" aria-label="Zamknij">
                            <pk-icon icon="times"></pk-icon>
                        </button>
                    ` : null}
                </div>
                <slot name="external-button"></slot>
            </div>
        `
    }
}

window.customElements.define('pk-toast-message', PkToastMessage);

export class PkToastContainer extends LitElement {

    static styles = css`
        :host {
            position: fixed;
            bottom: 0;
            left: 50%;
            translate: -50% 0;
            width: 70%;
            padding: 0 0 1.5rem 0;
            display: flex;
            flex-direction: column-reverse;
            gap: 0.3rem;
            z-index: 25555;
            pointer-events: none;
        }

        @media (max-width: 801px) {
            :host {
                width: 100vw;
                left: 0;
                translate: 0;
                padding-left: 0.5rem;
                padding-right: 0.5rem;
            }
        }
    `;

    provider = ProviderController.create(this, new ToastMessagesProvider());

    render() {
        const messages = this.provider.getAll();
        return html`
            <slot @hidden="${e => e.target?.parentElement?.removeChild(e.target)}"></slot>
            ${repeat(messages, (m) => m.id, (message) => html`
                <pk-toast-message
                    .type="${message.type}"
                    .duration="${message.duration}"
                    .icon="${message.icon}"
                    .iconType="${message.iconType}"
                    .iconCollection="${message.iconCollection}"
                    .open="${!message.isClosed}"
                    .closable="${message.closable !== false}"
                    .spinner="${message.spinner === true}"
                    @hidden="${() => {
                        this.provider.removeElement(message);
                    }}"
                >
                    ${unsafeHTML(message.message)}
                    ${message.actionHandler ? html`
                        <button @click="${(e) => {
                            message.actionHandler(e);
                            if (!e.defaultPrevented) {
                                e.target.closest('pk-toast-message').open = false;
                            }
                        }}" slot="button">${message.actionTitle || 'Akcja'}
                        </button>
                    ` : null}
                </pk-toast-message>
            `)}
        `;
    }
}

window.customElements.define('pk-toast-container', PkToastContainer);
